import React from 'react';
import { Box, HStack, Text, useBreakpointValue } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import { useHousePermissions } from '@/hooks/useHasPermission.ts';
import RecordPreviewContent from './RecordPreviewContent.tsx';

const SOURCE_BADGE_SIZE = '20px';
const DOCUMENT_THUMBNAIL_ASPECT = 1.414;

interface RecordPreviewItemProps {
	index: number;
	label: string;
	doc: Document | undefined;
	url: string | undefined;
	isHighlighted: boolean;
	highlightedAnnotationIndex: number | null | undefined;
	registerAnnotationRef: (index: number, element: HTMLElement | null) => void;
	/** Cited PDF pages for this document, sorted + de-duplicated. Empty means a document-level citation. */
	pages: number[];
	/** Opens this source's records detail drawer (the overflow menu's "Open" action). */
	onOpenDocument: (doc: Document, pages?: number[]) => void;
	/** Opens this source fullscreen at a cited page. Owned by the parent list so only one dialog mounts. */
	onOpenPage: (page: number) => void;
}

/**
 * A single source document entry in the sources drawer: a numbered badge, document preview/content,
 * page chips, and source actions.
 */
const RecordPreviewItem: React.FC<RecordPreviewItemProps> = (props: RecordPreviewItemProps) => {
	const { index, label, doc, url, isHighlighted, registerAnnotationRef, pages, onOpenDocument, onOpenPage } = props;
	const thumbnailWidth = useBreakpointValue({ base: 64, md: 76 }) ?? 76;
	const { canDownloadDocuments } = useHousePermissions();
	const [thumbnailAspect, setThumbnailAspect] = React.useState(DOCUMENT_THUMBNAIL_ASPECT);

	const firstCitedPage = pages[0] ?? 1;
	const thumbnailHeight = Math.round(thumbnailWidth * thumbnailAspect);

	return (
		<Box
			ref={(element: HTMLDivElement | null) => registerAnnotationRef(index, element)}
			tabIndex={-1}
			w="full"
			maxW="100%"
			scrollMarginBlock="96px"
			outline="none"
		>
			<HStack fontSize="sm" align="flex-start" w="full" maxW="100%" gap={{ base: 1, sm: 2 }}>
				<Text
					display="inline-flex"
					alignItems="center"
					justifyContent="center"
					boxSize={SOURCE_BADGE_SIZE}
					mt={3}
					flexShrink={0}
					fontWeight="semibold"
					color="fg.muted"
				>
					{`[${index + 1}]`}
				</Text>
				<RecordPreviewContent
					index={index}
					label={label}
					document={doc}
					url={url}
					pages={pages}
					isHighlighted={isHighlighted}
					thumbnailWidth={thumbnailWidth}
					thumbnailHeight={thumbnailHeight}
					firstCitedPage={firstCitedPage}
					canDownloadDocuments={canDownloadDocuments}
					onOpenDocument={onOpenDocument}
					onOpenPage={onOpenPage}
					onPreviewAspectChange={setThumbnailAspect}
				/>
			</HStack>
		</Box>
	);
};

export default RecordPreviewItem;
